import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase, parseRpcError } from '@/lib/supabaseClient'
import type { PortalContext, SelfServiceAllocation } from '@/lib/types'
import { Badge, Button, Card, Field, Input, PageSpinner, PortalStatusBadge, useConfirm, useToast, ToastProvider } from '@/components/ui'
import RoomTree, { GuestWriteResult } from '@/components/RoomTree'
import { formatDateTime } from '@/lib/format'

const FRIENDLY_ERRORS: Record<string, string> = {
  invalid_token: "This link isn't valid. Double-check the URL your event organizer sent you, or ask them to resend it.",
  access_code_not_configured: 'An access code is required for this link but none has been set up yet. Contact your event organizer.',
  invalid_access_code: 'That access code is incorrect. Please try again.',
  access_code_required: '',
  invalid_update_code: 'That update code is incorrect.',
  update_code_revoked: 'That update code has been revoked.',
  update_code_expired: 'That update code has expired.',
  update_code_exhausted: 'That update code has already been used the maximum number of times.',
}

/**
 * The public, secure company portal (sections 15-25). No Supabase Auth
 * session exists here -- every read and write goes through a portal_*
 * RPC that re-derives the company from a hashed token (see
 * migration 0009), so nothing here has to be trusted from the client.
 */
export default function CompanyPortal({ showUpdateCodePrompt = false }: { showUpdateCodePrompt?: boolean }) {
  return (
    <ToastProvider>
      <CompanyPortalInner showUpdateCodePrompt={showUpdateCodePrompt} />
    </ToastProvider>
  )
}

function CompanyPortalInner({ showUpdateCodePrompt }: { showUpdateCodePrompt: boolean }) {
  const { token } = useParams<{ token: string }>()
  const { push } = useToast()
  const { confirm, confirmDialog } = useConfirm()

  const [ctx, setCtx] = useState<PortalContext | null>(null)
  const [loading, setLoading] = useState(true)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [accessCode, setAccessCode] = useState('')
  const [accessCodeInput, setAccessCodeInput] = useState('')
  const [updateCode, setUpdateCode] = useState('')
  const [updateCodeInput, setUpdateCodeInput] = useState('')
  const [showUpdateCodeForm, setShowUpdateCodeForm] = useState(showUpdateCodePrompt)
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(
    async (code?: string, upCode?: string) => {
      if (!token) return
      setLoading(true)
      const { data, error } = await supabase.rpc('portal_get_context', {
        p_token: token,
        p_access_code: code ?? accessCode ?? null,
        p_update_code: upCode ?? updateCode ?? null,
      })
      setLoading(false)
      if (error) {
        const { code: errCode } = parseRpcError(error)
        setErrorCode(errCode)
        setCtx(null)
        return
      }
      setErrorCode(null)
      setCtx(data as PortalContext)
    },
    [token, accessCode, updateCode]
  )

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  async function submitAccessCode() {
    setAccessCode(accessCodeInput)
    await load(accessCodeInput, updateCode)
  }

  async function submitUpdateCode() {
    setSubmitting(true)
    const { error } = await supabase.rpc('portal_redeem_update_code', { p_token: token, p_access_code: accessCode || null, p_code: updateCodeInput })
    setSubmitting(false)
    if (error) {
      const { code } = parseRpcError(error)
      push(FRIENDLY_ERRORS[code] || code, 'error')
      return
    }
    setUpdateCode(updateCodeInput)
    setShowUpdateCodeForm(false)
    push('Update code accepted — you can now make changes')
    await load(accessCode, updateCodeInput)
  }

  async function saveGuest(guestId: string, fields: { full_name: string; mobile: string; email?: string | null }, expectedUpdatedAt: string): Promise<GuestWriteResult> {
    const { data, error } = await supabase.rpc('portal_update_guest', {
      p_token: token,
      p_access_code: accessCode || null,
      p_guest_id: guestId,
      p_full_name: fields.full_name || null,
      p_mobile: fields.mobile || null,
      p_email: fields.email || null,
      p_expected_updated_at: expectedUpdatedAt,
      p_update_code: updateCode || null,
    })
    if (error) {
      const { code, detail } = parseRpcError(error)
      return { ok: false, code, detail: detail || FRIENDLY_ERRORS[code] }
    }
    return { ok: true, updated: { updated_at: (data as any).updated_at } }
  }

  async function clearGuest(guestId: string, expectedUpdatedAt: string): Promise<GuestWriteResult> {
    const { data, error } = await supabase.rpc('portal_clear_guest', {
      p_token: token,
      p_access_code: accessCode || null,
      p_guest_id: guestId,
      p_expected_updated_at: expectedUpdatedAt,
      p_update_code: updateCode || null,
    })
    if (error) {
      const { code, detail } = parseRpcError(error)
      return { ok: false, code, detail: detail || FRIENDLY_ERRORS[code] }
    }
    return { ok: true, updated: { updated_at: (data as any).updated_at } }
  }

  async function moveGuest(fromId: string, toId: string): Promise<GuestWriteResult> {
    const { error } = await supabase.rpc('portal_move_guest', { p_token: token, p_access_code: accessCode || null, p_from_guest_id: fromId, p_to_guest_id: toId, p_update_code: updateCode || null })
    if (error) {
      const { code, detail } = parseRpcError(error)
      return { ok: false, code, detail: detail || FRIENDLY_ERRORS[code] }
    }
    return { ok: true }
  }

  async function addRoom(allocationId: string, checkIn: string, checkOut: string): Promise<GuestWriteResult> {
    const { error } = await supabase.rpc('portal_add_room', {
      p_token: token,
      p_access_code: accessCode || null,
      p_update_code: updateCode || null,
      p_allocation_id: allocationId,
      p_check_in: checkIn || null,
      p_check_out: checkOut || null,
    })
    if (error) {
      const { code, detail } = parseRpcError(error)
      return { ok: false, code, detail: detail || FRIENDLY_ERRORS[code] }
    }
    return { ok: true }
  }

  async function removeRoom(roomId: string): Promise<GuestWriteResult> {
    const { error } = await supabase.rpc('portal_remove_room', { p_token: token, p_access_code: accessCode || null, p_update_code: updateCode || null, p_room_id: roomId })
    if (error) {
      const { code, detail } = parseRpcError(error)
      return { ok: false, code, detail: detail || FRIENDLY_ERRORS[code] }
    }
    return { ok: true }
  }

  async function addGuestSlot(roomId: string): Promise<GuestWriteResult> {
    const { error } = await supabase.rpc('portal_add_guest_slot', { p_token: token, p_access_code: accessCode || null, p_update_code: updateCode || null, p_room_id: roomId })
    if (error) {
      const { code, detail } = parseRpcError(error)
      return { ok: false, code, detail: detail || FRIENDLY_ERRORS[code] }
    }
    return { ok: true }
  }

  async function submitFinal() {
    const ok = await confirm({
      title: 'Submit final rooming list?',
      description: "Once submitted, this rooming list will be locked. Contact the event organizer if you need to make further changes afterwards.",
      confirmLabel: 'Submit',
    })
    if (!ok) return
    const { error } = await supabase.rpc('portal_submit_final', { p_token: token, p_access_code: accessCode || null })
    if (error) {
      push(parseRpcError(error).code, 'error')
      return
    }
    push('Submitted — thank you!')
    load()
  }

  if (loading && !ctx) return <PageSpinner label="Loading your rooming list…" />

  if (errorCode === 'access_code_required') {
    return (
      <CenteredCard>
        <h1 className="mb-1 text-lg font-semibold text-amber-50">Enter Access Code</h1>
        <p className="mb-4 text-sm text-neutral-400">This rooming list link is protected. Enter the access code your event organizer gave you.</p>
        <Field label="Access Code" dark>
          <Input
            value={accessCodeInput}
            onChange={(e) => setAccessCodeInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitAccessCode()}
            autoFocus
            className="!border-neutral-700 !bg-neutral-900 !text-neutral-100 placeholder:!text-neutral-600 focus:!border-amber-500 focus:!ring-amber-500"
          />
        </Field>
        <Button variant="gold" className="mt-4 w-full" onClick={submitAccessCode}>
          Continue
        </Button>
      </CenteredCard>
    )
  }

  if (errorCode) {
    return (
      <CenteredCard>
        <h1 className="mb-2 text-lg font-semibold text-amber-50">Can't open this link</h1>
        <p className="text-sm text-neutral-400">{FRIENDLY_ERRORS[errorCode] || 'Something went wrong opening this rooming list. Please contact your event organizer.'}</p>
        {errorCode === 'invalid_access_code' && (
          <div className="mt-4">
            <Field label="Access Code" dark>
              <Input
                value={accessCodeInput}
                onChange={(e) => setAccessCodeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitAccessCode()}
                autoFocus
                className="!border-neutral-700 !bg-neutral-900 !text-neutral-100 placeholder:!text-neutral-600 focus:!border-amber-500 focus:!ring-amber-500"
              />
            </Field>
            <Button variant="gold" className="mt-3 w-full" onClick={submitAccessCode}>
              Try Again
            </Button>
          </div>
        )}
      </CenteredCard>
    )
  }

  if (!ctx) return <PageSpinner />

  const { event, company, portal, summary, hotels, self_service_allocations } = ctx
  const status = portal.effective_status
  const readOnlyMessage = status === 'closed' ? 'Submissions are closed.' : status === 'edit_only' ? 'New entries are closed — existing guests can still be edited.' : undefined

  const fillPct = summary.total_capacity > 0 ? Math.min(100, Math.round((summary.guests_entered / summary.total_capacity) * 100)) : 0

  return (
    <div
      className="min-h-screen pb-16"
      style={{
        backgroundColor: '#07060a',
        backgroundImage: 'radial-gradient(ellipse 900px 480px at 50% -8%, rgba(217,163,56,0.16), transparent 60%)',
      }}
    >
      <header
        className="border-b border-amber-900/30 bg-black/60 px-4 py-6 backdrop-blur sm:px-8"
        style={event.accent_color ? { borderTopWidth: 4, borderTopColor: event.accent_color } : undefined}
      >
        <div className="mx-auto max-w-4xl">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              {event.logo_url && <img src={event.logo_url} alt="" className="h-10 w-10 rounded-md object-contain" />}
              <div>
                <h1 className="text-lg font-semibold leading-tight text-amber-50">{event.name}</h1>
                <p className="text-sm text-neutral-400">
                  {company.name} <span className="text-neutral-600">({company.code})</span>
                  {event.venue ? ` · ${event.venue}` : ''}
                </p>
              </div>
            </div>
            <PortalStatusBadge status={status} />
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-8">
        {status === 'closed' && (
          <div className="mb-5 rounded-lg border border-red-900/40 bg-red-950/30 p-4 text-sm text-red-300">
            <p className="font-medium">This rooming list is closed for edits.</p>
            <p className="mt-1">{portal.submitted_at ? `Submitted ${formatDateTime(portal.submitted_at, portal.deadline_timezone)}.` : "The deadline has passed."} If you need to make a change, ask your event organizer for a temporary update code.</p>
            {!showUpdateCodeForm && (
              <Button size="sm" variant="secondary" className="mt-2 !border-red-800/50 !bg-transparent !text-red-300 hover:!bg-red-950/40" onClick={() => setShowUpdateCodeForm(true)}>
                I have an update code
              </Button>
            )}
          </div>
        )}
        {status === 'edit_only' && (
          <div className="mb-5 rounded-lg border border-amber-900/40 bg-amber-950/30 p-4 text-sm text-amber-300">
            You can still edit or move guests already entered, but new guests can no longer be added.
          </div>
        )}

        {showUpdateCodeForm && (
          <Card className="mb-5 !border-amber-900/30 !bg-neutral-900 p-4">
            <h3 className="mb-2 text-sm font-semibold text-amber-50">Enter Update Code</h3>
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[200px] flex-1">
                <Input
                  value={updateCodeInput}
                  onChange={(e) => setUpdateCodeInput(e.target.value)}
                  placeholder="e.g. UPD-12345"
                  className="!border-neutral-700 !bg-neutral-900 !text-neutral-100 placeholder:!text-neutral-600 focus:!border-amber-500 focus:!ring-amber-500"
                />
              </div>
              <Button variant="gold" loading={submitting} onClick={submitUpdateCode}>
                Unlock
              </Button>
              <Button variant="ghost" className="!text-neutral-400 hover:!bg-neutral-800" onClick={() => setShowUpdateCodeForm(false)}>
                Cancel
              </Button>
            </div>
          </Card>
        )}

        <Card className="mb-6 !border-amber-900/30 !bg-neutral-900/70 p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-amber-50">Progress</h2>
            {portal.deadline_at && status !== 'closed' && (
              <p className="text-xs text-neutral-500">
                Deadline: <span className="font-medium text-neutral-300">{formatDateTime(portal.deadline_at, portal.deadline_timezone)}</span> ({portal.deadline_timezone})
              </p>
            )}
          </div>

          <div className="mb-4">
            <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-800">
              <div className="h-full rounded-full bg-gradient-to-r from-amber-600 to-amber-400 transition-all" style={{ width: `${fillPct}%` }} />
            </div>
            <p className="mt-1.5 text-xs text-neutral-500">
              {summary.guests_entered} of {summary.total_capacity} guest slots filled ({fillPct}%)
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Rooms" value={summary.total_rooms} tone="slate" />
            <Stat label="Guest Capacity" value={summary.total_capacity} tone="blue" />
            <Stat label="Guests Entered" value={summary.guests_entered} tone="emerald" />
            <Stat label="Remaining Slots" value={summary.available_guest_slots} tone="amber" />
          </div>
        </Card>

        {portal.permissions.can_fill && self_service_allocations.length > 0 && (
          <SelfServicePanel allocations={self_service_allocations} onAddRoom={addRoom} onAdded={() => load()} />
        )}

        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-sm font-semibold text-amber-50">Your Rooms</h2>
          <p className="text-xs text-neutral-500">Click a hotel to expand it, then fill in each guest's name and mobile number.</p>
        </div>

        <RoomTree
          hotels={hotels}
          showPhysicalRoomNumbers={true}
          isAdmin={false}
          canFill={portal.permissions.can_fill}
          canEdit={portal.permissions.can_edit}
          canClear={false}
          canMove={portal.permissions.can_move}
          readOnlyMessage={readOnlyMessage}
          dark
          onSaveGuest={saveGuest}
          onClearGuest={clearGuest}
          onMoveGuest={moveGuest}
          onAddGuestSlot={addGuestSlot}
          onRemoveRoom={removeRoom}
          onDataChanged={() => load()}
        />

        {portal.require_final_submission && company.submission_status !== 'submitted' && status !== 'closed' && (
          <div className="mt-8 flex justify-end">
            <Button variant="gold" onClick={submitFinal}>
              Submit Final Rooming List
            </Button>
          </div>
        )}
        {company.submission_status === 'submitted' && (
          <div className="mt-6 flex justify-end">
            <Badge tone="gold">Submitted {portal.submitted_at ? formatDateTime(portal.submitted_at, portal.deadline_timezone) : ''}</Badge>
          </div>
        )}
      </div>
      {confirmDialog}
    </div>
  )
}

/**
 * "Add More Rooms" panel for self-service allocations (night_budget mode, or
 * fixed mode with unlimited_rooms). Shown above the room tree whenever the
 * company has at least one such allocation and can currently add guests --
 * this is purely additive to the existing rooming-list UI below it.
 */
function SelfServicePanel({
  allocations,
  onAddRoom,
  onAdded,
}: {
  allocations: SelfServiceAllocation[]
  onAddRoom: (allocationId: string, checkIn: string, checkOut: string) => Promise<GuestWriteResult>
  onAdded: () => void
}) {
  return (
    <Card className="mb-6 !border-amber-900/30 !bg-neutral-900/70 p-5">
      <h2 className="mb-1 text-sm font-semibold text-amber-50">Add More Rooms</h2>
      <p className="mb-4 text-xs text-neutral-500">These room types let you add your own rooms directly.</p>
      <div className="space-y-3">
        {allocations.map((a) => (
          <SelfServiceRow key={a.allocation_id} allocation={a} onAddRoom={onAddRoom} onAdded={onAdded} />
        ))}
      </div>
    </Card>
  )
}

function SelfServiceRow({
  allocation: a,
  onAddRoom,
  onAdded,
}: {
  allocation: SelfServiceAllocation
  onAddRoom: (allocationId: string, checkIn: string, checkOut: string) => Promise<GuestWriteResult>
  onAdded: () => void
}) {
  const [checkIn, setCheckIn] = useState('')
  const [checkOut, setCheckOut] = useState('')
  const [saving, setSaving] = useState(false)
  const { push } = useToast()

  const isNightBudget = a.allocation_mode === 'night_budget'
  const nightsRemaining = isNightBudget && a.night_budget != null ? Math.max(0, a.night_budget - a.nights_used) : null

  async function submit() {
    setSaving(true)
    const res = await onAddRoom(a.allocation_id, checkIn, checkOut)
    setSaving(false)
    if (res.ok) {
      setCheckIn('')
      setCheckOut('')
      push('Room added')
      onAdded()
    } else {
      push(res.detail || res.code || 'Could not add a room', 'error')
    }
  }

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-950/60 p-3.5">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium text-amber-50">
          {a.hotel_name} — {a.room_type_name}
        </div>
        {isNightBudget ? (
          <Badge tone="gold">
            {a.nights_used} / {a.night_budget ?? '∞'} night{a.night_budget === 1 ? '' : 's'} used
          </Badge>
        ) : (
          <Badge tone="gold">{a.room_count} room{a.room_count === 1 ? '' : 's'} so far</Badge>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-2.5">
        <div>
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-neutral-500">Check-in</label>
          <Input
            type="date"
            className="h-9 !border-neutral-700 !bg-neutral-900 !text-neutral-100 focus:!border-amber-500 focus:!ring-amber-500"
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-neutral-500">Check-out</label>
          <Input
            type="date"
            className="h-9 !border-neutral-700 !bg-neutral-900 !text-neutral-100 focus:!border-amber-500 focus:!ring-amber-500"
            value={checkOut}
            onChange={(e) => setCheckOut(e.target.value)}
          />
        </div>
        <Button variant="gold" size="sm" loading={saving} onClick={submit} disabled={isNightBudget && nightsRemaining === 0}>
          + Add Room
        </Button>
      </div>
      {isNightBudget && nightsRemaining != null && (
        <p className="mt-2 text-xs text-neutral-500">
          {nightsRemaining > 0 ? `${nightsRemaining} night(s) remaining in your budget.` : 'You have used your full nights budget.'}
        </p>
      )}
    </div>
  )
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      style={{
        backgroundColor: '#07060a',
        backgroundImage: 'radial-gradient(ellipse 900px 480px at 50% -8%, rgba(217,163,56,0.16), transparent 60%)',
      }}
    >
      <Card className="w-full max-w-sm !border-amber-900/30 !bg-neutral-900 p-6 shadow-xl">{children}</Card>
    </div>
  )
}

const STAT_TONES: Record<string, string> = {
  slate: 'border-neutral-700 bg-neutral-800/60 text-neutral-100',
  blue: 'border-sky-800/50 bg-sky-950/40 text-sky-300',
  emerald: 'border-emerald-800/50 bg-emerald-950/40 text-emerald-300',
  amber: 'border-amber-700/50 bg-amber-950/40 text-amber-300',
}

function Stat({ label, value, tone = 'slate' }: { label: string; value: number; tone?: keyof typeof STAT_TONES }) {
  return (
    <div className={`rounded-lg border px-3 py-3 text-center ${STAT_TONES[tone]}`}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="mt-0.5 text-[11px] uppercase tracking-wide opacity-70">{label}</div>
    </div>
  )
}
